import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Divider,
  Alert,
  useTheme,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Stop as StopIcon,
  Flag as FlagIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchExecutionsForTrade,
  fetchAdjustmentsForTrade,
  selectExecutionsForTrade,
  selectAdjustmentsForTrade,
  selectExecutionsLoading,
  selectAdjustmentsLoading,
  selectExecutionsError,
  selectAdjustmentsError
} from './executionsSlice';
import DWLFLoader from '../../components/DWLFLoader';

const ExecutionsTimelineSimple = ({ tradeId }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  
  const executions = useSelector(state => selectExecutionsForTrade(state, tradeId));
  const adjustments = useSelector(state => selectAdjustmentsForTrade(state, tradeId));
  const executionsLoading = useSelector(selectExecutionsLoading);
  const adjustmentsLoading = useSelector(selectAdjustmentsLoading);
  const executionsError = useSelector(selectExecutionsError);
  const adjustmentsError = useSelector(selectAdjustmentsError);

  useEffect(() => {
    dispatch(fetchExecutionsForTrade({ tradeId }));
    dispatch(fetchAdjustmentsForTrade({ tradeId }));
  }, [dispatch, tradeId]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getExecutionIcon = (type) => {
    switch (type) {
      case 'entry':
      case 'add':
        return <AddIcon />;
      case 'reduce':
      case 'exit':
        return <RemoveIcon />;
      case 'stop_hit':
      case 'tp_hit':
        return <CloseIcon />;
      case 'fee':
        return <MoneyIcon />;
      default:
        return <AddIcon />;
    }
  };

  const getExecutionColor = (type) => {
    switch (type) {
      case 'entry':
      case 'add':
        return 'success';
      case 'reduce':
      case 'exit':
        return 'error';
      case 'stop_hit':
      case 'tp_hit':
        return 'warning';
      case 'fee':
        return 'info';
      default:
        return 'primary';
    }
  };

  const getAdjustmentIcon = (field) => {
    switch (field) {
      case 'stop':
        return <StopIcon />;
      case 'takeProfit':
        return <TrendingUpIcon />;
      case 'label':
      case 'tag':
        return <FlagIcon />;
      default:
        return <EditIcon />;
    }
  };

  const getAdjustmentColor = (field) => {
    switch (field) {
      case 'stop':
        return 'warning';
      case 'takeProfit':
        return 'success';
      case 'label':
      case 'tag':
        return 'info';
      default:
        return 'primary';
    }
  };

  const calculateExecutionPnl = (execution, previousExecutions = []) => {
    if (execution.type === 'fee') return -execution.fee;
    
    // Calculate average entry price from previous executions
    let totalQty = 0;
    let totalCost = 0;
    
    for (const prevExec of previousExecutions) {
      if (['entry', 'add'].includes(prevExec.type)) {
        totalQty += prevExec.qty;
        totalCost += prevExec.qty * prevExec.price + (prevExec.fee || 0);
      } else if (['reduce', 'exit'].includes(prevExec.type)) {
        totalQty -= prevExec.qty;
        totalCost = totalCost * (totalQty / (totalQty + prevExec.qty));
      }
    }
    
    const avgEntry = totalQty > 0 ? totalCost / totalQty : 0;
    
    if (['reduce', 'exit'].includes(execution.type)) {
      const exitQty = execution.qty;
      const exitValue = exitQty * execution.price - (execution.fee || 0);
      const entryValue = exitQty * avgEntry;
      return exitValue - entryValue;
    }
    
    return 0;
  };

  // Combine and sort all events chronologically
  const allEvents = [
    ...executions.map(exec => ({ ...exec, type: 'execution', eventType: exec.type })),
    ...adjustments.map(adj => ({ ...adj, type: 'adjustment', eventType: adj.field }))
  ].sort((a, b) => new Date(a.ts) - new Date(b.ts));

  if (executionsLoading || adjustmentsLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <DWLFLoader size="medium" />
      </Box>
    );
  }

  if (executionsError || adjustmentsError) {
    return (
      <Alert severity="error">
        {executionsError || adjustmentsError}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Trade Timeline
      </Typography>
      
      {allEvents.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">
            No executions or adjustments yet. Use the Quick Actions to add your first execution.
          </Typography>
        </Paper>
      ) : (
        <List>
          {allEvents.map((event, index) => {
            const isExecution = event.type === 'execution';
            const previousExecutions = executions.slice(0, index);
            const pnl = isExecution ? calculateExecutionPnl(event, previousExecutions) : null;
            
            return (
              <ListItem key={`${event.type}-${event.executionId || event.adjustmentId}`} sx={{ mb: 2 }}>
                <ListItemIcon>
                  <Avatar 
                    sx={{ 
                      bgcolor: isExecution ? getExecutionColor(event.eventType) : getAdjustmentColor(event.eventType),
                      width: 40,
                      height: 40
                    }}
                  >
                    {isExecution ? getExecutionIcon(event.eventType) : getAdjustmentIcon(event.eventType)}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box>
                      <Typography variant="h6" component="span">
                        {isExecution ? (
                          <>
                            {event.eventType === 'entry' && 'Entry'}
                            {event.eventType === 'add' && 'Add Position'}
                            {event.eventType === 'reduce' && 'Reduce Position'}
                            {event.eventType === 'exit' && 'Exit'}
                            {event.eventType === 'stop_hit' && 'Stop Hit'}
                            {event.eventType === 'tp_hit' && 'Take Profit Hit'}
                            {event.eventType === 'fee' && 'Fee'}
                          </>
                        ) : (
                          <>
                            {event.eventType === 'stop' && 'Stop Loss'}
                            {event.eventType === 'takeProfit' && 'Take Profit'}
                            {event.eventType === 'label' && 'Label'}
                            {event.eventType === 'tag' && 'Tag'}
                          </>
                        )}
                      </Typography>
                      
                      {isExecution ? (
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                          {event.qty && (
                            <Grid size={{ xs: 6 }}>
                              <Chip 
                                size="small" 
                                label={`${event.qty} shares`}
                                color="primary"
                                variant="outlined"
                              />
                            </Grid>
                          )}
                          {event.price && (
                            <Grid size={{ xs: 6 }}>
                              <Chip 
                                size="small" 
                                label={`$${event.price}`}
                                color="info"
                                variant="outlined"
                              />
                            </Grid>
                          )}
                          {event.fee > 0 && (
                            <Grid size={{ xs: 6 }}>
                              <Chip 
                                size="small" 
                                label={`Fee: $${event.fee}`}
                                color="warning"
                                variant="outlined"
                              />
                            </Grid>
                          )}
                          {pnl !== null && pnl !== 0 && (
                            <Grid size={{ xs: 6 }}>
                              <Chip 
                                size="small" 
                                label={`P&L: $${pnl.toFixed(2)}`}
                                color={pnl >= 0 ? 'success' : 'error'}
                                variant="outlined"
                              />
                            </Grid>
                          )}
                        </Grid>
                      ) : (
                        <Grid container spacing={1} sx={{ mt: 1 }}>
                          <Grid size={{ xs: 12 }}>
                            <Chip 
                              size="small" 
                              label={`${event.old || 'None'} → ${event.new}`}
                              color="secondary"
                              variant="outlined"
                            />
                          </Grid>
                        </Grid>
                      )}
                      
                      {event.memo && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          {event.memo}
                        </Typography>
                      )}
                      
                      {event.reason && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          Reason: {event.reason}
                        </Typography>
                      )}
                    </Box>
                  }
                  secondary={formatDate(event.ts)}
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default ExecutionsTimelineSimple;
